import { AuthenticatedRoute } from '@/auth/AuthenticatedRoute';
import { Fragment, lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { isFabricaPlatformPath } from '@/routes/fabricaPlatformPaths';
import { AppShell } from '@/components/layout/AppShell';
import { Loading } from '@/components/feedback/Loading';
import { useRole } from '@/hooks/useRole';
import { portalRoutes, renderRoutes } from '@/routes';
import { MenuAccessGuard } from '@/routes/MenuAccessGuard';

const NotFound = lazy(() => import('@/pages/NotFound'));
const Landing = lazy(() => import('@/pages/public/Landing'));
const PublicApplication = lazy(() => import('@/pages/public/PublicApplication'));
const SignInPage = lazy(() => import('@/pages/fabrica/SignInPage'));
const FabricaShell = lazy(() =>
  import('@/pages/fabrica/FabricaShell').then((m) => ({ default: m.FabricaShell })),
);
const FabricaOrganizations = lazy(() => import('@/pages/fabrica/OrganizationsPage'));
const FabricaUsersRoles = lazy(() => import('@/pages/fabrica/UsersRolesPage'));
const FabricaMenusOperations = lazy(() => import('@/pages/fabrica/MenusOperationsPage'));
const FabricaPermissions = lazy(() => import('@/pages/fabrica/PermissionsPage'));
/** The ported Fabrica platform (src/fabrica): its own shell and routes, on its own top-level paths. */
const FabricaPlatform = lazy(() => import('@fabrica-root'));

/** Public pages, outside the app shell (no sidebar or header) and open to every role. */
const PUBLIC_PATHS = {
  /** The marketing landing page; its Login button opens the Administrator workspace. */
  landing: '/',
  /** The online admission application a family fills in. */
  apply: '/admissionForm/apply',
  /** Sign-in; with ?admin=true a Fabrica developer lands in the Fabrica admin portal. */
  signIn: '/signin',
} as const;

/** Mounts only the routes of the portals the current role reaches — see `src/routes` — plus the public pages. */
export function App() {
  const { portals } = useRole();
  const { pathname } = useLocation();

  // The Fabrica platform routes with DebtCollectify's absolute paths and its own catch-all, so it mounts
  // instead of Camptain's routes rather than inside them. It sends a visitor without a session to sign-in.
  if (isFabricaPlatformPath(pathname)) {
    return (
      <Suspense fallback={<Loading minHeight="100dvh" />}>
        <FabricaPlatform />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<Loading minHeight="100dvh" />}>
      <Routes>
        <Route path={PUBLIC_PATHS.landing} element={<Landing />} />
        <Route path={PUBLIC_PATHS.apply} element={<PublicApplication />} />
        <Route path={PUBLIC_PATHS.signIn} element={<SignInPage />} />
        {/* The Fabrica admin portal: Fabrica developers only (FabricaShell checks; the API checks again). */}
        <Route element={<AuthenticatedRoute />}>
          <Route path="/fabrica" element={<FabricaShell />}>
            <Route index element={<Navigate to="organizations" replace />} />
            <Route path="organizations" element={<FabricaOrganizations />} />
            <Route path="users" element={<FabricaUsersRoles />} />
            <Route path="menus" element={<FabricaMenusOperations />} />
            <Route path="permissions" element={<FabricaPermissions />} />
          </Route>
          <Route element={<AppShell />}>
            {/* Each page follows the role's Fabrica menus once they are loaded. */}
            <Route element={<MenuAccessGuard />}>
              {portals.map((portal) => (
                <Fragment key={portal}>{renderRoutes(portalRoutes[portal])}</Fragment>
              ))}
            </Route>
            <Route path="*" element={<NotFound />} />
          </Route>
        </Route>
      </Routes>
    </Suspense>
  );
}
